import React, { useState } from "react";
import {
  Button,
  FlexRowBetween,
  Input,
  Label,
  PopupForm,
  TextArea,
} from "../../common/Style";
import { useForm } from "react-hook-form";
import { CFV } from "../../common/others/Functions";
import OpacityLoading from "../../common/loader/OpacityLoading";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { add } from "../../../store/reducers/todosReducer";
import GCalendar from "../../common/calendar/Calendar";
import GenericModal from "../../common/modals/GenericModal";

// סוגי הודעות למשתמשים עם role 6 – כל אחד יציג תצוגה מקדימה (תמונה תתווסף בהמשך)
// הסדר חובה: הודעה ראשונה → הודעה שניה → ווצאפ ראשון → ווצאפ שני → תזכורת (סמס/ווצאפ) → תודה
export const ROLE_6_MESSAGE_TYPES = [
  { id: "sms_first", label: "הודעה ראשונה" },
  { id: "sms_second", label: "הודעה שניה" },
  { id: "whatsapp_first", label: "ווצאפ ראשון" },
  { id: "whatsapp_second", label: "ווצאפ שני" },
  { id: "sms_reminder", label: "תזכורת סמס" },
  { id: "whatsapp_reminder", label: "תזכורת ווצאפ" },
  { id: "thank_you_sms", label: "תודה סמס" },
];

/** מחזיר YYYY-MM-DD לתאריך (להשוואת ימים). */
export const getTaskDateStr = (executionDate) => {
  if (!executionDate) return "";
  const d =
    typeof executionDate === "string" ? new Date(executionDate) : executionDate;
  return d.toISOString().slice(0, 10);
};

const ROLE_6_BEFORE_EVENT_TYPES = [
  "הודעה ראשונה",
  "הודעה שניה",
  "ווצאפ ראשון",
  "ווצאפ שני",
];

/**
 * בודק תאריך עבור משימות role 6 לפי סוג:
 * - הודעה ראשונה/שניה, ווצאפ ראשון/שני: התאריך חייב להיות עד תאריך האירוע (לא אחריו).
 * - תזכורת סמס / תזכורת ווצאפ: רק בתאריך האירוע.
 * - תודה סמס: רק בתאריך האירוע או אחריו.
 */
export const validateRole6ReminderAndThankDate = (
  eventDate,
  executionDate,
  title
) => {
  if (!eventDate) return { valid: true };
  const eventStr = getTaskDateStr(eventDate);
  const execStr = getTaskDateStr(executionDate);
  if (!eventStr || !execStr) return { valid: true };

  if (ROLE_6_BEFORE_EVENT_TYPES.includes(title)) {
    if (execStr > eventStr) {
      return {
        valid: false,
        message: "לא ניתן להוסיף משימה מסוג זה בתאריך לאחר תאריך האירוע",
      };
    }
  }
  if (title === "תזכורת סמס" || title === "תזכורת ווצאפ") {
    if (execStr !== eventStr) {
      return {
        valid: false,
        message: "ניתן להוסיף תזכורת רק בתאריך האירוע",
      };
    }
  }
  if (title === "תודה סמס") {
    if (execStr < eventStr) {
      return {
        valid: false,
        message: "ניתן להוסיף תודה רק בתאריך האירוע או אחריו",
      };
    }
  }
  return { valid: true };
};

/**
 * מחזיר את קבוצת הכותרות (סוגי הודעות) שכבר קיימות בלו"ז – כל סוג יכול להופיע פעם אחת בלבד.
 */
export const getExistingLabelsInSchedule = (todos, excludeTodoId = null) => {
  const list = Array.isArray(todos) ? todos : Object.values(todos || {});
  const filtered = list.filter((t) => t._id !== excludeTodoId);
  return new Set(filtered.map((t) => t.title));
};

/**
 * בודק שסדר התאריכים של משימות role 6 נשמר:
 * תאריך הודעה ראשונה <= תאריך הודעה שניה <= תאריך ווצאפ ראשון <= ... <= תאריך תודה.
 * בהוספה/עריכה: התאריך של המשימה הנוכחית חייב להיות גדול או שווה מכל סוג קודם, וקטן או שווה מכל סוג מאוחר.
 * @param todos - אובייקט או מערך של todos (של המשתמש)
 * @param executionDate - תאריך הביצוע של המשימה שנוספת/מתערכת
 * @param newTitle - כותרת המשימה (סוג ההודעה)
 * @param excludeTodoId - בעריכה: _id של המשימה שמעריכים (לא נספרת)
 */
export const validateRole6TaskOrder = (
  todos,
  executionDate,
  newTitle,
  excludeTodoId = null,
) => {
  const todosList = Array.isArray(todos) ? todos : Object.values(todos || {});
  const existing = todosList.filter((t) => t._id !== excludeTodoId);
  const dateStrNew = getTaskDateStr(executionDate);
  if (!dateStrNew) return { valid: true };

  const labels = ROLE_6_MESSAGE_TYPES.map((t) => t.label);
  const minDateStrByLabel = {};
  const maxDateStrByLabel = {};
  existing.forEach((t) => {
    const ds = getTaskDateStr(t.executionDate);
    if (!ds) return;
    if (minDateStrByLabel[t.title] == null || ds < minDateStrByLabel[t.title]) {
      minDateStrByLabel[t.title] = ds;
    }
    if (maxDateStrByLabel[t.title] == null || ds > maxDateStrByLabel[t.title]) {
      maxDateStrByLabel[t.title] = ds;
    }
  });

  const idx = labels.indexOf(newTitle);
  if (idx < 0) return { valid: true };

  // הודעה ראשונה, הודעה שניה, ווצאפ ראשון, ווצאפ שני – רק אחת מכל הסוגים האלה ביום (הפרש של יום בין כל אחת)
  const onePerDayTypes = [
    "הודעה ראשונה",
    "הודעה שניה",
    "ווצאפ ראשון",
    "ווצאפ שני",
  ];
  if (onePerDayTypes.includes(newTitle)) {
    const otherOnSameDay = existing.some(
      (t) =>
        onePerDayTypes.includes(t.title) &&
        getTaskDateStr(t.executionDate) === dateStrNew,
    );
    if (otherOnSameDay) {
      return {
        valid: false,
        message:
          "הודעה ראשונה, הודעה שניה, ווצאפ ראשון וווצאפ שני חייבים להיות בימים שונים. בתאריך זה כבר קיימת משימה מסוג זה.",
      };
    }
  }

  // סוגים קודמים: התאריך (היום) המוקדם ביותר שלהם חייב להיות <= התאריך שבחרת
  for (let i = 0; i < idx; i++) {
    const label = labels[i];
    const minDs = minDateStrByLabel[label];
    if (minDs != null && minDs > dateStrNew) {
      const displayDate = minDs.split("-").reverse().join("/");
      return {
        valid: false,
        message: `תאריך "${label}" (${displayDate}) חייב להיות לפני התאריך שבחרת`,
      };
    }
  }

  // סוגים מאוחרים: התאריך (היום) המאוחר ביותר שלהם חייב להיות >= התאריך שבחרת
  for (let i = idx + 1; i < labels.length; i++) {
    const label = labels[i];
    const maxDs = maxDateStrByLabel[label];
    if (maxDs != null && maxDs < dateStrNew) {
      const displayDate = maxDs.split("-").reverse().join("/");
      return {
        valid: false,
        message: `תאריך "${label}" (${displayDate}) חייב להיות אחרי התאריך שבחרת`,
      };
    }
  }

  // תודה סמס: חייבת להיות לפחות משימת תזכורת אחת (סמס או ווצאפ) עם תאריך <= התאריך שבחרת
  if (idx === 6) {
    const minReminderDs =
      minDateStrByLabel["תזכורת סמס"] != null &&
      minDateStrByLabel["תזכורת ווצאפ"] != null
        ? minDateStrByLabel["תזכורת סמס"] < minDateStrByLabel["תזכורת ווצאפ"]
          ? minDateStrByLabel["תזכורת סמס"]
          : minDateStrByLabel["תזכורת ווצאפ"]
        : (minDateStrByLabel["תזכורת סמס"] ??
          minDateStrByLabel["תזכורת ווצאפ"]);
    if (minReminderDs == null) {
      return {
        valid: false,
        message:
          "יש להוסיף קודם לפחות משימת תזכורת אחת (תזכורת סמס או תזכורת ווצאפ)",
      };
    }
    if (minReminderDs > dateStrNew) {
      return {
        valid: false,
        message: "תאריך משימת התזכורת חייב להיות לפני התאריך שבחרת",
      };
    }
  }

  return { valid: true };
};

const Add = ({ init, producer, userRole, isTal, eventDate }) => {
  const dispatch = useDispatch();
  const { todos, todosLoading } = useSelector((state) => state.todosReducer);
  const isRole6 = userRole === 6 || (userRole === 1 && isTal);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: isRole6
      ? isTal
        ? { title: "", messageTypeId: "" }
        : {
            title: ROLE_6_MESSAGE_TYPES[0].label,
            messageTypeId: ROLE_6_MESSAGE_TYPES[0].id,
          }
      : {},
  });
  const [showCalendar, setShowCalendar] = useState(null);
  const [date, setDate] = useState(new Date());
  const existingLabelsInSchedule =
    isRole6 ? getExistingLabelsInSchedule(todos, null) : new Set();

  const selectedMessageTypeId = isRole6 && !isTal ? watch("messageTypeId") : null;
  const selectedMessageType = isRole6
    ? isTal
      ? ROLE_6_MESSAGE_TYPES.find((t) => t.label === (watch("title") || "").trim())
      : ROLE_6_MESSAGE_TYPES.find((t) => t.id === selectedMessageTypeId)
    : null;

  const handleSetDate = (e) => {
    let date1 = e;
    date1.setHours(20);
    setDate(date1);
  };

  const onSubmit = async (data) => {
    let title;
    if (isRole6) {
      title = isTal
        ? (data.title || "").trim()
        : (ROLE_6_MESSAGE_TYPES.find((t) => t.id === data.messageTypeId)?.label ??
          data.title);
      if (!title) {
        toast.warning("יש להזין כותרת");
        return;
      }
      const labelsFromList = new Set(ROLE_6_MESSAGE_TYPES.map((t) => t.label));
      if (labelsFromList.has(title) && existingLabelsInSchedule.has(title)) {
        toast.warning(`משימה מסוג "${title}" כבר קיימת בלו\"ז`);
        return;
      }
      const dateCheck = validateRole6ReminderAndThankDate(
        eventDate,
        date.toISOString(),
        title
      );
      if (!dateCheck.valid) {
        toast.warning(dateCheck.message);
        return;
      }
      const orderCheck = validateRole6TaskOrder(
        todos,
        date.toISOString(),
        title,
        null,
      );
      if (!orderCheck.valid) {
        toast.warning(orderCheck.message);
        return;
      }
    } else {
      title = data.title;
    }
    const body = {
      ...data,
      title,
      executionDate: date.toISOString(),
      company: producer,
      ...(isRole6 && { description: "" }),
    };
    if (isRole6) delete body.messageTypeId;
    dispatch(add(body)).then((res) => {
      if (!res.payload?.error) {
        toast.success("הפעולה בוצעה בהצלחה");
        init();
      } else toast.error(res.payload?.error);
    });
  };

  return (
    <PopupForm
      onClick={(e) => e.stopPropagation()}
      onSubmit={handleSubmit(onSubmit)}
      className="animate__animated animate__fadeIn"
    >
      <div
        style={{
          width: "90%",
          margin: "15px auto",
        }}
      >
        {isRole6 ? (
          <>
            {isTal ? (
              <>
                <Label>כותרת</Label>
                <Input
                  type="text"
                  {...register("title", {
                    required: true,
                    minLength: 2,
                    maxLength: 50,
                  })}
                />
                {CFV("title", "בין 2 ל־50 תווים", errors)}
                <Label style={{ marginTop: "12px", display: "block" }}>
                  בחירה מהירה מהרשימה
                </Label>
                <select
                  onChange={(e) => {
                    const option = ROLE_6_MESSAGE_TYPES.find(
                      (t) => t.id === e.target.value
                    );
                    if (option) setValue("title", option.label, { shouldValidate: true });
                  }}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: "16px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    backgroundColor: "#fff",
                    marginBottom: "8px",
                  }}
                >
                  <option value="">— בחר להזין כותרת —</option>
                  {ROLE_6_MESSAGE_TYPES.map((type) => (
                    <option
                      key={type.id}
                      value={type.id}
                      disabled={existingLabelsInSchedule.has(type.label)}
                    >
                      {type.label}
                      {existingLabelsInSchedule.has(type.label) ? " (קיים בלו\"ז)" : ""}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <Label>סוג הודעה</Label>
                <select
                  {...register("messageTypeId", { required: true })}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: "16px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    backgroundColor: "#fff",
                    marginBottom: "8px",
                  }}
                >
                  {ROLE_6_MESSAGE_TYPES.map((type) => (
                    <option
                      key={type.id}
                      value={type.id}
                      disabled={existingLabelsInSchedule.has(type.label)}
                    >
                      {type.label}
                      {existingLabelsInSchedule.has(type.label) ? " (קיים בלו\"ז)" : ""}
                    </option>
                  ))}
                </select>
              </>
            )}

            <Label style={{ marginTop: "16px", display: "block" }}>
              תצוגה מקדימה – מה האורחים יקבלו
            </Label>
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                padding: "12px",
                minHeight: "120px",
                backgroundColor: "#f9f9f9",
                marginBottom: "16px",
              }}
            >
              {selectedMessageType && (
                <>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#555",
                      marginBottom: "8px",
                    }}
                  >
                    {selectedMessageType.label}
                  </div>
                  <img
                    src={`/message-previews/${selectedMessageType.id}.jpeg`}
                    alt={`תצוגה מקדימה - ${selectedMessageType.label}`}
                    onError={(e) => {
                      e.target.style.display = "none";
                      const fallback = e.target.nextElementSibling;
                      if (fallback) fallback.style.display = "block";
                    }}
                    style={{ maxWidth: "100%", borderRadius: "6px" }}
                  />
                  <div
                    className="message-preview-placeholder"
                    style={{
                      display: "none",
                      fontSize: "13px",
                      color: "#888",
                      padding: "20px",
                      textAlign: "center",
                    }}
                  >
                    תמונת תצוגה מקדימה תופיע כאן (יש להוסיף קובץ{" "}
                    <code>{selectedMessageType.id}.jpeg</code> בתיקיית
                    public/message-previews)
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <Label>כותרת</Label>
            <Input
              type="text"
              {...register("title", {
                required: true,
                minLength: 2,
                maxLength: 50,
              })}
            />
            {CFV("title", "בין 2 ל 50 תווים", errors)}
          </>
        )}

        {!isRole6 && (
          <>
            <Label>הערות</Label>
            <TextArea
              type="text"
              rows="3"
              {...register("description", {
                minLength: 0,
                maxLength: 500,
              })}
            />
            {CFV("description", "עד 500 תווים", errors)}
          </>
        )}

        <Label>תאריך לביצוע</Label>
        <Input
          onClick={() => setShowCalendar(true)}
          value={date
            .toISOString()
            .replace(/T.*/, "")
            .split("-")
            .reverse()
            .join("/")}
          onChange={() => {}}
          type={"text"}
        />

        <FlexRowBetween style={{ marginTop: "50px" }}>
          <Button style={{ width: "100%" }}>הוספה</Button>
        </FlexRowBetween>
      </div>

      {todosLoading && <OpacityLoading />}

      {showCalendar && (
        <GenericModal
          init={setShowCalendar}
          div={
            <GCalendar
              setDate={handleSetDate}
              date={date}
              init={setShowCalendar}
            />
          }
        />
      )}
    </PopupForm>
  );
};

export default Add;
